import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function GetStarted() {
  const [form, setForm] = useState({ name: "", email: "", company: "", business_size: "Solopreneur", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email.includes("@")) {
      toast.error("Please fill name and a valid email");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/demo-request`, form);
      setDone(true);
      toast.success("All set! We'll email your trial details within 5 minutes.");
    } catch (err) {
      toast.error("Something went wrong. Try again.");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div data-testid="get-started-success" className="min-h-[calc(100vh-5rem)] hero-gradient flex items-center justify-center px-6">
        <div className="text-center max-w-md">
          <div className="w-20 h-20 rounded-full bg-[#12b571] flex items-center justify-center mx-auto mb-6">
            <Check size={40} className="text-white" />
          </div>
          <h1 className="font-display text-4xl md:text-5xl font-extrabold text-black tracking-tight">You're in.</h1>
          <p className="mt-4 text-lg text-black/70">
            Check <span className="font-semibold text-black">{form.email}</span> for your trial activation link.
          </p>
          <Link to="/" className="btn-primary mt-8">Back to home <ArrowRight size={18} /></Link>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="get-started-page" className="min-h-[calc(100vh-5rem)] grid lg:grid-cols-5">
      <div className="lg:col-span-3 hero-gradient p-8 lg:p-16 flex items-center grain relative overflow-hidden">
        <div className="absolute -bottom-32 -right-32 w-96 h-96 rounded-full bg-[#FFE4E8] opacity-50 blur-3xl animate-blob" />
        <div className="relative max-w-xl">
          <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-extrabold text-black tracking-tight leading-[1.05]">
            Start your free<br />
            <span className="italic text-black/60 font-medium">14-day Brivo trial.</span>
          </h1>
          <p className="mt-5 text-lg text-black/70 leading-relaxed">
            One simple form, no credit card. We'll have you running your business through Brivo within the hour.
          </p>
          <ul className="mt-9 space-y-4">
            {[
              "Full access to every feature for 14 days",
              "No credit card required up front",
              "Free white-glove migration help",
              "Cancel anytime, no questions asked",
            ].map((b) => (
              <li key={b} className="flex items-start gap-3 text-black">
                <Check size={20} className="mt-0.5 flex-shrink-0" />
                <span className="font-medium">{b}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="lg:col-span-2 bg-white p-8 lg:p-12 flex items-center">
        <form onSubmit={onSubmit} className="w-full space-y-5">
          <div>
            <label className="text-sm font-semibold text-black mb-2 block">Your name *</label>
            <input
              data-testid="get-started-name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full px-4 py-3 rounded-xl border border-[#12b571]/15 focus:border-[#12b571] focus:outline-none transition-colors bg-[#FFFCF2]"
              placeholder="Adunni Ola"
            />
          </div>
          <div>
            <label className="text-sm font-semibold text-black mb-2 block">Work email *</label>
            <input
              data-testid="get-started-email"
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="w-full px-4 py-3 rounded-xl border border-[#12b571]/15 focus:border-[#12b571] focus:outline-none transition-colors bg-[#FFFCF2]"
              placeholder="you@business.com"
            />
          </div>
          <div>
            <label className="text-sm font-semibold text-black mb-2 block">Company</label>
            <input
              data-testid="get-started-company"
              value={form.company}
              onChange={(e) => setForm({ ...form, company: e.target.value })}
              className="w-full px-4 py-3 rounded-xl border border-[#12b571]/15 focus:border-[#12b571] focus:outline-none transition-colors bg-[#FFFCF2]"
              placeholder="Lush Boutique"
            />
          </div>
          <div>
            <label className="text-sm font-semibold text-black mb-2 block">Business size</label>
            <select
              data-testid="get-started-size"
              value={form.business_size}
              onChange={(e) => setForm({ ...form, business_size: e.target.value })}
              className="w-full px-4 py-3 rounded-xl border border-[#12b571]/15 focus:border-[#12b571] focus:outline-none transition-colors bg-[#FFFCF2]"
            >
              <option>Solopreneur</option>
              <option>Small (2-10)</option>
              <option>Medium (11-50)</option>
              <option>Large (51-200)</option>
              <option>Enterprise (200+)</option>
            </select>
          </div>
          <button
            data-testid="get-started-submit"
            type="submit"
            disabled={loading}
            className="btn-primary w-full justify-center disabled:opacity-60"
          >
            {loading ? "Setting things up..." : "Start free trial"} <ArrowRight size={18} />
          </button>
          <p className="text-xs text-center text-black/50">
            By continuing you agree to our Terms of Service and Privacy Policy.
          </p>
        </form>
      </div>
    </div>
  );
}
